import React from 'react';

/**
 * Button — the single primary/secondary/ghost/danger action primitive.
 * Keyboard focus is handled by the global :focus-visible outline (index.css),
 * so variants intentionally don't define their own focus ring.
 */
const VARIANTS = {
    primary: 'bg-brand-800 hover:bg-brand-900 text-white shadow-md',
    secondary: 'bg-white dark:bg-slate-700 text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-slate-600 hover:bg-gray-50 dark:hover:bg-slate-600 shadow-sm',
    outline: 'border-2 border-brand-700 dark:border-brand-500 text-brand-800 dark:text-brand-400 bg-transparent hover:bg-brand-50 dark:hover:bg-brand-900/30',
    ghost: 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700',
    danger: 'bg-red-600 hover:bg-red-700 text-white shadow-md',
};

const SIZES = {
    sm: 'py-1.5 px-3 text-sm',
    md: 'py-2 px-4 text-sm',
    lg: 'py-3 px-6 text-base',
    xl: 'py-4 px-8 text-lg',
};

export default function Button({
    as: Tag = 'button',
    variant = 'primary',
    size = 'md',
    pill = false,
    fullWidth = false,
    icon: Icon,
    iconRight: IconRight,
    className = '',
    type = 'button',
    children,
    ...props
}) {
    const classes = [
        'inline-flex items-center justify-center font-semibold transition-all duration-150',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        'active:scale-[0.97]',
        pill ? 'rounded-full' : 'rounded-lg',
        fullWidth ? 'w-full' : '',
        VARIANTS[variant] || VARIANTS.primary,
        SIZES[size] || SIZES.md,
        className,
    ].filter(Boolean).join(' ');

    // `type` only applies to real <button> elements.
    const typeProp = Tag === 'button' ? { type } : {};

    return (
        <Tag className={classes} {...typeProp} {...props}>
            {Icon && <Icon className={`h-5 w-5 ${children ? 'mr-2' : ''}`} />}
            {children}
            {IconRight && <IconRight className={`h-5 w-5 ${children ? 'ml-2' : ''}`} />}
        </Tag>
    );
}
